/**
 * Service layer for Theme.
 * This service centralizes the state, orchestration, or data access used by its feature area.
 */
import { DOCUMENT } from '@angular/common';
import { Injectable, effect, inject, signal } from '@angular/core';

/**
 * SERVICE: Theme
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);

  readonly theme = signal<'light' | 'dark'>(this.resolveInitial());

  /**
   * BUSINESS LOGIC: constructor
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  constructor() {
    /** STATE OWNER: reactiveEffect documents the effect state boundary used by this file. */
    effect(() => this.apply(this.theme()));
  }

  /**
   * BUSINESS LOGIC: setTheme
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  setTheme(value: 'light' | 'dark'): void {
    localStorage.setItem('preferredTheme', value);
    this.theme.set(value);
  }

  /**
   * BUSINESS LOGIC: resolveInitial
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private resolveInitial(): 'light' | 'dark' {
    const stored = localStorage.getItem('preferredTheme');
    return stored === 'dark' ? 'dark' : 'light';
  }

  /**
   * BUSINESS LOGIC: apply
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private apply(currentTheme: 'light' | 'dark'): void {
    const html = this.document.documentElement;
    html.setAttribute('data-theme', currentTheme);
    html.setAttribute('data-color-scheme', currentTheme);
    html.style.colorScheme = currentTheme;

    if (currentTheme === 'dark') {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }
  }
}
